import React, { useState } from "react";
import { message } from "antd";

import { FileDropzone, Modal } from "../../../../components/shared";
import type { FileRejectReason } from "../../../shared/FileDropzone/FileDropzone";
import {
  BANNER_IMAGE_CONTENT_TYPE,
  MAX_BANNER_IMAGE_BYTES,
  uploadBannerImage,
} from "../../../../utils/bannerImageUpload";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";

const MAX_KB = Math.floor(MAX_BANNER_IMAGE_BYTES / 1024);

const rejectCopy = (): Record<FileRejectReason, string> => ({
  type: AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_REJECT_TYPE,
  size: formatString(AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_REJECT_SIZE, {
    maxKb: String(MAX_KB),
  }),
});

export type UploadImageModalProps = {
  open: boolean;
  onClose: () => void;
  /** Called with the CloudFront URL once the creative is uploaded to S3. */
  onUploaded: (imageUrl: string) => void;
};

/**
 * "Upload Image" modal for the campaign banner editor (BONENG-522): drop or
 * browse for a PNG creative, which is uploaded to S3 via a presigned PUT the
 * moment it's picked; the resulting CloudFront URL is handed back through
 * `onUploaded` (the caller fills the banner's Image URL field with it).
 */
const UploadImageModal: React.FC<UploadImageModalProps> = ({
  open,
  onClose,
  onUploaded,
}) => {
  const [uploading, setUploading] = useState(false);
  // Bumped every time the modal closes: an in-flight upload started in an
  // earlier "session" must not fill the field (or toast) after the operator
  // dismissed the modal — they may have typed a different URL meanwhile.
  const sessionRef = React.useRef(0);
  React.useEffect(() => {
    if (!open) {
      sessionRef.current += 1;
      // Reset so a reopened modal is immediately usable instead of showing
      // "Uploading…" until the abandoned request settles — its result is
      // discarded by the session guard anyway.
      setUploading(false);
    }
  }, [open]);

  const handleChange = async (file: File | null) => {
    if (!file) return;
    const session = sessionRef.current;
    setUploading(true);
    try {
      const imageUrl = await uploadBannerImage(file);
      if (session !== sessionRef.current) return;
      message.success(AppStrings.PAM_GENERIC_REQUEST_SUCCEEDED);
      onUploaded(imageUrl);
      onClose();
    } catch (error) {
      if (session !== sessionRef.current) return;
      message.error(
        error instanceof Error
          ? error.message
          : AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_FAIL,
      );
    } finally {
      // A stale settle must not clear the flag for an upload the operator
      // started after reopening the modal.
      if (session === sessionRef.current) setUploading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_IMAGE}
      size="lg"
      data-testid="upload-image-modal"
    >
      <FileDropzone
        value={null}
        onChange={handleChange}
        accept={BANNER_IMAGE_CONTENT_TYPE}
        maxBytes={MAX_BANNER_IMAGE_BYTES}
        onReject={(_rejected, reason) => message.error(rejectCopy()[reason])}
        disabled={uploading}
        heading={
          uploading
            ? AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOADING
            : AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_DROPZONE
        }
        hint={formatString(AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_HINT, {
          maxKb: String(MAX_KB),
        })}
        buttonLabel={AppStrings.BROWSE_FILE}
        inputAriaLabel={AppStrings.PAM_DYNAMIC_BONUS_BANNER_UPLOAD_ARIA}
        data-testid="upload-image-dropzone"
      />
    </Modal>
  );
};

export default UploadImageModal;
